<template>
  <div v-if="visiblePerksQty">
    <div v-if="isBcom || isProductPerksEnabledPhase2 || visiblePerksQty > 1" class="h4 values-title margin-bottom-xs" role="heading" aria-level="4">
      {{ title }}
    </div>
    <div :class="['perks-wrapper', { 'perks-wrapper--redesign': isProductPerksEnabledPhase2 }]">
      <WnmBanner v-if="isWnmVisible" />
      <PromotionPerkPhase2 v-if="isPromotionsVisible && isProductPerksEnabledPhase2 && isBcom" />
      <ShopMoreFacade v-if="isShopMoreVisible" :class="{ 'margin-bottom-xxs': !isProductPerksEnabledPhase2 }" />
      <ClientOnly>
        <JoinStarRewardsPerkFacade v-if="isStarRewardsVisible" />
        <ShippingAndReturnsPerksFacade v-if="isShippingAndReturnsPerksVisible" />
        <Pzo v-if="isPzoVisible" ref="pzo-component" />
      </ClientOnly>
      <PromotionPerk v-if="isPromotionsVisible && !(isProductPerksEnabledPhase2 && isBcom)" />
      <Ppc
        v-if="isPpcVisible"
        :is-perks-placement="isBcom && isPpcVisible"
        :class="['perks-ppc', { 'margin-bottom-xxs': !isProductPerksEnabledPhase2 }]"
      />
      <template v-if="selectedPrice?.flags?.creditFinance">
        <KlarnaPerk v-if="isKlarnaVisible && isProductPerksEnabledPhase2 && isBcom" />
        <CcfpPerk v-if="isCcfpVisible" />
        <KlarnaPerk v-if="isKlarnaVisible && !(isProductPerksEnabledPhase2 && isBcom)" />
      </template>
    </div>
  </div>
</template>
<script setup>
/* istanbul ignore file */
import { computed, ref, watch, useTemplateRef } from 'vue';

import useKlarna from '@nuxt-common/media-card-large/app/modules/credit-finance/composables/useKlarna';
import useCcfp from '@nuxt-common/media-card-large/app/modules/credit-finance/composables/useCcfp';

import ShippingAndReturnsPerksFacade from '~/modules/productPerks/components/ShippingAndReturnsPerksFacade';
import Pzo from '~/modules/pzo/components/PzoView';
import usePzo from '~/modules/pzo/composables/usePzo';
import { useProductPrice } from '~/modules/price/composables/useProductPrice';
import Ppc from '~/modules/ppc/components/PPC.vue';
import { usePpc } from '~/modules/ppc/composables/usePPC';
import ShopMoreFacade from '~/modules/shop-more/components/ShopMoreFacade';
import { useProductFlags } from '~/modules/featureControl/composables/useProductFlags';
import useFeatureEligibility from '~/modules/featureControl/composables/useFeatureEligibility';

import { useBrand } from '~/composables/useBrand';
import useExperiments from '~/stores/useExperimentsStore';
import { useLoyalty } from '~/composables/loyalty/useLoyalty';
import useLoyalList from '~/composables/useLoyalList';

import WnmBanner from '~/modules/worry-no-more/components/WnmBanner';
import { useWnm } from '~/modules/worry-no-more/composables/useWnm';

import PromotionPerk from './PromotionPerk';
import PromotionPerkPhase2 from './PromotionPerkPhase2';
import KlarnaPerk from './KlarnaPerk';
import CcfpPerk from './CcfpPerk';
import JoinStarRewardsPerkFacade from './JoinStarRewardsPerkFacade';

const pzoComponent = useTemplateRef('pzo-component');
const selectedPrice = useProductPrice();
const { isProductPerksEnabledPhase2 } = useFeatureEligibility();

const { isMcom, isBcom } = useBrand();
const { showPzo } = usePzo();
const { isShowPpc, isPowerPointsBelowPointsThreshold, isLoyaltyPromoPresent } = usePpc();
const { priceWithinRange } = useKlarna(selectedPrice);
const { isCcfp } = useCcfp(selectedPrice);
const { isUserNonLoyalist } = useLoyalty();
const { isDataFromAEM, isShippingBanner } = useLoyalList();

const {
  experimentFlags: { isShopMoreExpandedEnabled, isGWPEnabled, isGWPEnabled2 },
} = useExperiments();
const { isBigTicketItemFlag } = useProductFlags();

const { isWnmVisible } = useWnm();

const isShopMoreVisible = isMcom && isShopMoreExpandedEnabled;
const isStarRewardsVisible = computed(() => {
  const isBaseValid = isProductPerksEnabledPhase2 && !isBigTicketItemFlag && isUserNonLoyalist.value;
  const isStoreValid = isMcom || (isBcom && isShippingBanner.value);

  return isBaseValid && isStoreValid;
});

const isShippingAndReturnsPerksVisible = ref(isProductPerksEnabledPhase2 && !isBigTicketItemFlag && isBcom && isDataFromAEM.value);

watch(
  [isUserNonLoyalist],
  ([isUserNonLoyalistUpdated]) => {
    isStarRewardsVisible.value = isStarRewardsVisible.value && isUserNonLoyalistUpdated;
    isShippingAndReturnsPerksVisible.value = isShippingAndReturnsPerksVisible.value && isUserNonLoyalistUpdated;
  },
  { once: true },
);

const isPerkVisibleByPriceCondition = computed(() => isMcom || (isBcom && !selectedPrice.value?.flags?.showOffersAvailableMessage));
const isPzoVisible = computed(() => showPzo && isPerkVisibleByPriceCondition.value);
const isPromotionsVisible = computed(() => {
  const perksPromotionsLength = selectedPrice.value?.perksPromotions?.length;

  if (isGWPEnabled || isGWPEnabled2) {
    const gwpPromotions = selectedPrice.value?.gwpPromotions?.length;
    return !!perksPromotionsLength && perksPromotionsLength > gwpPromotions && isPerkVisibleByPriceCondition.value;
  }

  return !!perksPromotionsLength && isPerkVisibleByPriceCondition.value;
});
const isPpcVisible = computed(
  () =>
    (isMcom && isShowPpc.value && (!isProductPerksEnabledPhase2 || isBigTicketItemFlag)) ||
    (isBcom &&
      isShowPpc.value &&
      isPerkVisibleByPriceCondition.value &&
      (!isProductPerksEnabledPhase2 || isBigTicketItemFlag) &&
      ((!selectedPrice.value?.offersData?.topOffer.isLoyallistOffer && selectedPrice.value?.offersData?.topOffer?.description) ||
        isPowerPointsBelowPointsThreshold.value ||
        !isLoyaltyPromoPresent.value)),
);
const isCcfpVisible = computed(() => isCcfp && isPerkVisibleByPriceCondition.value);
const isKlarnaVisible = computed(() => selectedPrice.value?.flags.klarna && priceWithinRange.value && isPerkVisibleByPriceCondition.value);

const perksMap = computed(() => ({
  // finance
  klarna: isKlarnaVisible.value,
  ccfp: isCcfpVisible.value,
  // other
  wnm: isWnmVisible.value,
  pzo: isPzoVisible.value && pzoComponent?.value?.pzoContentAvailable,
  ppc: isPpcVisible.value,
  promotions: isPromotionsVisible.value,
  starRewards: isStarRewardsVisible.value,
  shipping: isShippingAndReturnsPerksVisible.value,
  shopMore: isShopMoreVisible,
}));

const visiblePerksQty = computed(() => Object.values(perksMap.value).filter(Boolean).length);

const TITLE_SERVICES = 'Services & Offers';
const TITLE_FINANCING = 'Financing options';
const TITLE_OFFERS_MCOM = 'Offers & perks';
const TITLE_OFFERS_BCOM = 'Offers & Perks';

const title = computed(() => {
  if (isWnmVisible.value) return TITLE_SERVICES;

  const { klarna, ccfp, ...others } = perksMap.value;

  const hasFinance = klarna || ccfp;
  const hasAnythingElse = Object.values(others).some(Boolean);

  if (hasFinance && !hasAnythingElse && isProductPerksEnabledPhase2) {
    return TITLE_FINANCING;
  }

  return isMcom ? TITLE_OFFERS_MCOM : TITLE_OFFERS_BCOM;
});
</script>

<style lang="scss" scoped>
.perks-wrapper {
  &.perks-wrapper--redesign {
    overflow: hidden;

    .perks-ppc {
      &:not(:first-child) {
        position: relative;
        &:before {
          position: absolute;
          content: '';
          top: -1px;
          left: 52px;
          width: calc(100% - 52px);
          height: 0.5px;
          background-color: $gray-2-color;
        }
      }
    }
  }
}
</style>

<style lang="scss" brand="mcom" scoped>
.perks-wrapper {
  &.perks-wrapper--redesign {
    border-radius: 5px;
  }
}
</style>

<style lang="scss" brand="bcom" scoped>
@import '~settingSCSS';

.perks-wrapper {
  .perks-ppc {
    :deep(.loyalty-wrapper) {
      width: 100%;
      @include padding-direction('vertical', map-get($spacer, 'xs'));
    }
  }

  &.perks-wrapper--redesign {
    .perks-ppc {
      &:not(:first-child) {
        &:before {
          left: 36px;
          width: calc(100% - 36px);
        }
      }
    }
  }
}
</style>
